import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';

const ThemeContext = createContext();

export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within ThemeProvider');
    }
    return context;
};

export const ThemeModeProvider = ({ children }) => {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

    // Get initial theme from localStorage or default to 'light'
    const [themeMode, setThemeMode] = useState(() => {
        const saved = localStorage.getItem('themeMode');
        return (saved === 'dark' || saved === 'light') ? saved : 'light';
    });

    // Effective theme is the same as themeMode (no system mode)
    const effectiveTheme = themeMode;

    // Update localStorage when theme changes
    useEffect(() => {
        localStorage.setItem('themeMode', themeMode);
    }, [themeMode]);

    // Update HTML meta theme-color based on theme
    useEffect(() => {
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            // Use Gobel orange for light mode, dark gray for dark mode
            metaThemeColor.setAttribute('content', effectiveTheme === 'dark' ? '#1e1e1e' : '#fc8c14');
        }
    }, [effectiveTheme]);

    const toggleTheme = () => {
        setThemeMode(current => current === 'light' ? 'dark' : 'light');
    };

    const value = {
        themeMode,
        effectiveTheme,
        setThemeMode,
        toggleTheme,
        isDarkMode: effectiveTheme === 'dark'
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
